<template>
  <div class="d-flex justify-content-between align-items-center">
    <div class="input-wrapper" @click="enableOrderLineInput" :class="{ 'input-disabled': disabled || locked, 'input-locked': locked }">
      <input
        :type="type"
        class="form-control"
        :class="[classString, {'has-dropdown': hasDropdownData}]"
        :disabled="disabled || locked"
        :style="styleString"
        @change="changed = true"
        @blur="handleBlur"
        @keyup.enter="blurInput"
        :ref="`orderlineInputField-${rowIndex}`"
        v-model="value"
      >
      <button v-if="disabled && !locked" class="enable-input-btn"></button>
      <!-- Dropdown indicator icon - altijd tonen als hasDropdownData true is -->
      <div v-if="hasDropdownData && !locked" class="dropdown-indicator" @click.stop="toggleDropdown">
        <i class="bi bi-caret-down-fill"></i>
      </div>
      <!-- Clear button - alleen tonen als veld niet disabled is én waarde niet leeg is -->
      <button v-if="!disabled && !locked && value && value !== ''" @mousedown.prevent.stop="prepareToClear" @mouseup.stop="clearInput" class="clear-input-btn">
        <i class="bi bi-x"></i>
      </button>
      
      <!-- Dropdown voor items -->
      <div v-if="showDropdown && dropdownData && dropdownData.length > 0 && !clearingInProgress" class="dropdown-menu-custom">
        <div
          v-for="(item, idx) in dropdownData"
          :key="idx"
          class="dropdown-item-custom"
          @mousedown.prevent="selectDropdownItem(item)"
          @mouseenter="checkTextOverflow"
          :ref="`dropdownItem-${idx}`"
          :data-bs-toggle="'tooltip'"
          :data-bs-placement="'right'"
          :data-bs-title="item.number + ') ' + getDropdownItemLabel(item)"
          data-bs-custom-class="simple-tooltip"
        >
          {{ item.number }}) {{ getDropdownItemLabel(item) }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>

export default {
  name: 'EditOrderlineProp',
  // rowIndex is important for blurring the input field
  props: {
    orderlineProperty: Object,
    classStr: String,
    styleStr: String,
    round: Number,
    type: String,
    rowIndex: Number,
    // Nieuwe props voor dropdown items
    dropdownItemsKey: {
      type: String,
      default: 'data'
    },
    valueKey: {
      type: String,
      default: 'id'
    },
    labelKey: {
      type: String,
      default: 'address'
    },
    tooltipNextLineSeparator: {
      type: String,
      default: ','
    },
    locked: {
      type: Boolean,
      default: false
    }
  },
  emits: ['changed'],
  data() {
    return {
      orderlineProp: {},
      changed: false,
      classString: '',
      styleString: '',
      value: null,
      disabled: true,
      clearInProgress: false,
      clearingInProgress: false,
      showDropdown: false,
      dropdownData: [],
      isSelectingItem: false,
      hasDropdownData: false
    }
  },
  watch: {
    orderlineProperty() {
      this.init();
    },
    value() {
      // Toon dropdown items alleen als de waarde leeg is en er items zijn
      this.updateDropdownItems();
    }
  },
  methods: {
    toggleDropdown() {
      if (this.locked) return;
      // Als het veld disabled is, maak het dan eerst actief
      if (this.disabled && this.hasDropdownData) {
        this.enableOrderLineInput();
        return;
      }

      // Als het veld al actief is, toggle de dropdown
      if (!this.disabled && this.hasDropdownData) {
        this.showDropdown = !this.showDropdown;
        if (this.showDropdown) {
          this.$nextTick(() => {
            this.initTooltips();
            this.positionDropdownForMobile();
          });
        }
      }
    },
    updateDropdownItems() {
      // Controleer of er dropdown items zijn in de orderlineProperty
      if (this.orderlineProp &&
          this.orderlineProp[this.dropdownItemsKey] &&
          this.orderlineProp[this.dropdownItemsKey].length > 0) {

        // Stel hasDropdownData in op true om het indicator icon te tonen
        this.hasDropdownData = true;

        // Bereid dropdown data voor
        this.dropdownData = this.orderlineProp[this.dropdownItemsKey];
        //add numbering before each item
        this.dropdownData.forEach((item, index) => {
          item.number = index + 1;
        });

        // Toon dropdown alleen als input actief is
        if (!this.disabled) {
          this.showDropdown = true;

          // Initialiseer tooltips nadat dropdown items zijn bijgewerkt
          this.$nextTick(() => {
            this.initTooltips();
            this.positionDropdownForMobile();
          });
        } else {
          this.showDropdown = false;
        }
      } else {
        this.hasDropdownData = false;
        this.showDropdown = false;
      }
    },
    getDropdownItemLabel(item) {
      const label = item[this.labelKey] || 'Onbekend';
      const value = item[this.valueKey];
      // Als er zowel een value als label is en ze verschillen, toon beide
      if (value && label && value !== label) {
        return `${value} - ${label}`;
      }
      return label || value || 'Onbekend';
    },
    selectDropdownItem(item) {
      console.log('Dropdown item geselecteerd:', item);
      
      // Verwijder alle tooltips voordat we het item selecteren
      this.hideAllTooltips();
      
      this.isSelectingItem = true;
      this.value = item[this.valueKey];
      this.showDropdown = false;
      this.changed = true;
      
      // Wacht even voordat we de input disablen
      setTimeout(() => {
        this.isSelectingItem = false;
        this.disableInput();
      }, 100);
    },
    hideAllTooltips() {
      // Verwijder alle tooltips in het document
      if (typeof window !== 'undefined' && typeof window.bootstrap !== 'undefined') {
        const tooltips = document.querySelectorAll('.tooltip');
        tooltips.forEach(tooltip => {
          tooltip.remove();
        });
        
        // Als er Bootstrap Tooltip instances zijn, vernietig ze
        const tooltipTriggers = this.$el.querySelectorAll('[data-bs-toggle="tooltip"]');
        tooltipTriggers.forEach(el => {
          const tooltipInstance = window.bootstrap.Tooltip.getInstance(el);
          if (tooltipInstance) {
            tooltipInstance.dispose();
          }
        });
      }
    },
    enableOrderLineInput() {
      if (this.locked) return;
      console.log(`enableOrderLineInput() orderlineInputField-${this.rowIndex}`)
      this.disabled = false;
      this.$nextTick(() => {
        this.$refs[`orderlineInputField-${this.rowIndex}`].focus();
        this.updateDropdownItems(); // Toon dropdown items als de waarde leeg is
      });
    },
    handleBlur() {
      // Verwijder tooltips bij blur
      this.hideAllTooltips();
      
      // Wacht even voordat we de input disablen
      setTimeout(() => {
        if (!this.clearInProgress && !this.isSelectingItem) {
          this.disableInput();
        }
      }, 200);
    },
    disableInput() {
      this.disabled = true;
      this.showDropdown = false;
      this.hideAllTooltips(); // Verwijder tooltips bij het disablen van de input
      this.saveOrderlineProp();
    },
    async saveOrderlineProp() {
      if(this.changed) {
        this.$emit('changed', this.value, this.rowIndex);
      }
    },
    blurInput() {
      this.$refs[`orderlineInputField-${this.rowIndex}`].blur();
    },
    prepareToClear() {
      this.clearInProgress = true;
      this.clearingInProgress = true;
    },
    clearInput() {
      this.value = '';
      this.clearInProgress = false;
      this.orderlineProp[this.valueKey] = '';
      
      setTimeout(() => {
        this.clearingInProgress = false;
        if (!this.disabled) {
          this.updateDropdownItems();
        }
      }, 300);
    },
    init() {
      this.orderlineProp = this.orderlineProperty
      this.styleString = this.styleStr
      this.classString = this.classStr
      if(this.round !== undefined) {
        let val = this.orderlineProperty.value
        if(typeof val !== 'number' ) {
          val = Number(val)
        }
        this.value = val.toFixed(this.round)
      } else {
        this.value = this.orderlineProperty.value
      }
      
      // Initialiseer dropdown items
      this.updateDropdownItems();
    },
    addTooltipStyles() {
      // Voeg een stylesheet toe voor de aangepaste tooltips als deze nog niet bestaat
      if (!document.getElementById('simple-tooltip-styles')) {
        const style = document.createElement('style');
        style.id = 'simple-tooltip-styles';
        style.textContent = `
          .simple-tooltip {
            opacity: 1 !important;
          }
          .simple-tooltip .tooltip-inner {
            background-color: #d9dada !important;
            color: #212529 !important; /* Zwarte tekstkleur */
            border: 1px solid #dee2e6 !important;
            font-size: 0.675rem !important;
            padding: 0.25rem 0.5rem !important;
            max-width: 250px !important;
            box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075) !important;
          }
          .simple-tooltip .tooltip-arrow {
            display: none !important;
          }
        `;
        document.head.appendChild(style);
      }
    },
    initTooltips() {
      if (typeof window !== 'undefined' && typeof window.bootstrap !== 'undefined') {
        const tooltipTriggerList = [].slice.call(this.$el.querySelectorAll('[data-bs-toggle="tooltip"]'));
        tooltipTriggerList.forEach(tooltipTriggerEl => {
          new window.bootstrap.Tooltip(tooltipTriggerEl, {
            html: true // Sta HTML toe in tooltips
          });
        });
      }
    },
    formatTooltipText(text) {
      if (!text) return '';
      
      // Vervang \n door <br>
      return text.split(this.tooltipNextLineSeparator).map(line => line.trim()).join('<br>') || text
    },
    
    formatDropdownItemHTML(text) {
      if (!text) return '';

      // Vervang \n door <br>
      return text.replace(/\n/g, '<br>');
    },
    checkTextOverflow(event) {
      const element = event.target;

      // Check of tekst afgekapt is
      const isOverflowing = element.scrollWidth > element.clientWidth;

      // Verkrijg of maak tooltip instance
      if (typeof window !== 'undefined' && typeof window.bootstrap !== 'undefined') {
        let tooltipInstance = window.bootstrap.Tooltip.getInstance(element);

        if (isOverflowing) {
          // Als tekst afgekapt is en er is geen tooltip, maak er een
          if (!tooltipInstance) {
            tooltipInstance = new window.bootstrap.Tooltip(element, {
              html: true,
              customClass: 'simple-tooltip'
            });
          }
          // Toon de tooltip
          tooltipInstance.show();
        } else {
          // Als tekst niet afgekapt is, verberg/verwijder tooltip
          if (tooltipInstance) {
            tooltipInstance.hide();
          }
        }
      }
    },
    // Nieuwe methode om dropdown goed te positioneren op mobiel
    positionDropdownForMobile() {
      // Alleen uitvoeren als de dropdown zichtbaar is
      if (!this.showDropdown) return;
      
      setTimeout(() => {
        const isMobile = window.innerWidth <= 768; // iPad portretmodus (was 576px)
        const dropdown = this.$el.querySelector('.dropdown-menu-custom');
        if (!dropdown) return;
        
        if (isMobile) {
          // Reset eerst alle stijlen
          dropdown.style.position = '';
          dropdown.style.top = '';
          dropdown.style.left = '';
          dropdown.style.width = '';
          
          // Bereken de positie gebaseerd op de input
          const input = this.$refs[`orderlineInputField-${this.rowIndex}`];
          if (input) {
            const rect = input.getBoundingClientRect();
            dropdown.style.position = 'fixed';
            dropdown.style.top = `${rect.bottom}px`;
            dropdown.style.left = `${Math.max(15, rect.left)}px`;
            dropdown.style.width = `${Math.min(window.innerWidth - 100, 650)}px`; // Aangepast aan 100px marge
          }
        } else {
          // Reset stijlen voor desktop
          dropdown.style.position = '';
          dropdown.style.top = '';
          dropdown.style.left = '';
          dropdown.style.width = '';
        }
      }, 10);
    },
  },
  created() {
    this.init();
  },
  mounted() {
    // Voeg de aangepaste tooltip stijlen toe
    this.addTooltipStyles();
    
    // Initialiseer tooltips
    this.$nextTick(() => {
      this.initTooltips();
    });
    
    // Luister naar resize events voor mobiele positionering
    window.addEventListener('resize', this.positionDropdownForMobile);
  },
  updated() {
    // Initialiseer tooltips opnieuw wanneer de component is bijgewerkt
    this.$nextTick(() => {
      this.initTooltips();
    });
  },
  beforeUnmount() {
    // Verwijder alle tooltips wanneer de component wordt verwijderd
    this.hideAllTooltips();
    
    // Verwijder resize event listener
    window.removeEventListener('resize', this.positionDropdownForMobile);
  }
}
</script>

<style scoped>
.enable-input-btn {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  opacity: 0;
  cursor: pointer;
}

.input-wrapper {
  position: relative;
  display: inline-block;
}

.input-disabled input {
  cursor: pointer;
}

.input-locked input {
  cursor: not-allowed;
  background-color: #f1f3f5;
}

.input-locked .enable-input-btn,
.input-locked .clear-input-btn,
.input-locked .dropdown-indicator {
  display: none;
}

.clear-input-btn {
  position: absolute;
  right: 0.5em;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  width: 1.5em;
  height: 1.5em;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: #f8f9fa;
  z-index: 2; /* Zorg ervoor dat deze boven andere elementen komt */
}

.clear-input-btn:hover {
  background-color: #e0e0e0;
}

.clear-input-btn i {
  font-size: 1em;
  color: #6c757d;
}

/* Styling voor dropdown indicator icon - nu links van de clear button */
.dropdown-indicator {
  position: absolute;
  right: 2.25em; /* Ruimte voor clear button + wat extra ruimte */
  top: 50%;
  transform: translateY(-50%);
  width: 1.5em;
  height: 1.5em;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8em;
  color: #6c757d;
  cursor: pointer; /* Maak het klikbaar */
  z-index: 2;
}

.dropdown-indicator:hover {
  color: #495057; /* Donkerder bij hover */
}

/* Pas padding aan op basis van aanwezigheid van icons */
.input-wrapper input {
  padding-right: 2em; /* Standaard ruimte voor alleen dropdown icon */
}

.input-wrapper input.has-dropdown {
  padding-right: 4em; /* Extra ruimte voor zowel dropdown als clear button */
}

/* Stijlen voor de dropdown */
.dropdown-menu-custom {
  position: absolute;
  top: 100%;
  left: -200px;
  right: auto;
  min-width: 100%; /* Minimaal zo breed als het input veld */
  width: max-content; /* Breedte bepaald door content */
  max-width: min(650px, calc(100vw - 300px)); /* Niet breder dan viewport - marge */
  max-height: 200px;
  overflow-y: auto;
  overflow-x: auto; /* Horizontale scroll indien nodig */
  background-color: white;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  z-index: 1000;
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);
}

/* Zorg ervoor dat dropdown items met HTML correct worden weergegeven */
.dropdown-item-custom {
  white-space: normal; /* Sta tekstomloop toe voor HTML-inhoud */
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 0.5rem;
  cursor: pointer;
  min-width: 300px; /* Verhoogde minimale breedte voor dropdown items */
  width: auto; /* Laat de items groeien indien nodig */
}

.dropdown-item-custom:hover {
  background-color: #f8f9fa;
}

/* Zorg ervoor dat tooltips boven andere elementen worden weergegeven */
:global(.light-tooltip) {
  z-index: 9999;
}

:global(.light-tooltip .tooltip-inner) {
  background-color: white;
  color: #212529;
  border: 1px solid #dee2e6;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

:global(.light-tooltip .tooltip-arrow::before) {
  border-right-color: #dee2e6;
}

:global(.light-tooltip .tooltip-arrow::after) {
  border-right-color: white;
  content: "";
  position: absolute;
  left: 1px;
}

/* Responsive gedrag voor kleine schermen zoals iPhone en iPad in portretmodus */
@media (max-width: 768px) {
  .dropdown-menu-custom {
    max-width: calc(100vw - 100px); /* Niet breder dan viewport - marge */
    left: 0;
    right: 0;
    min-width: auto;
    width: auto;
  }
  
  .dropdown-item-custom {
    min-width: auto;
    width: 100%;
  }
}
</style>